import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Admin CRUD for the 4-tier (Essential / Experience / Grand / Custom) budget
 * selection the customer sees on the planner. Names, emojis, descriptions,
 * and ranges are all editable so we can tune positioning without a redeploy.
 */
export default function BudgetTiersAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/budget-tiers")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!confirm(`Remove "${t.label}" from the customer budget selection?`)) return;
    try {
      await api.delete(`/admin/budget-tiers/${t.id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="budget-tiers-admin">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1 mb-2">
            <Wallet className="w-3.5 h-3.5" strokeWidth={2.25} />
            <span className="text-[10px] uppercase tracking-widest font-bold">Planner · Step 4</span>
          </div>
          <p className="text-sm text-charcoal/70">
            Customers pick one of these before AI generation. The AI uses the range as guidance;
            the final plan flags any inventory item priced above the range with
            <span className="mx-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
              Closest available · ₹X above your budget
            </span>.
          </p>
          <p className="text-xs text-charcoal/50 mt-2">
            The Custom row is a special tier — its Min/Max are entered by the customer at runtime.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowDialog(true); }}
          className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
          data-testid="add-budget-tier-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={2.25} /> Add tier
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-6 text-charcoal/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-charcoal/60" data-testid="budget-tiers-empty">
            No tiers yet.
          </div>
        ) : (
          <div className="divide-y divide-warm">
            {items.map((t, i) => (
              <div key={t.id} className="p-4 flex items-center gap-4" data-testid={`budget-tier-row-${i}`}>
                <span className="text-2xl leading-none flex-shrink-0" aria-hidden>{t.emoji || "🎈"}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg text-charcoal">{t.label}
                    {t.is_custom && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-rose-500">Custom</span>}
                  </p>
                  <p className="text-xs text-charcoal/60">{t.description}</p>
                  <p className="text-sm text-charcoal/70 mt-1">
                    {t.is_custom ? "Customer sets Min/Max at runtime"
                                 : `₹${t.min?.toLocaleString("en-IN")}–₹${t.max?.toLocaleString("en-IN")}`}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditing(t); setShowDialog(true); }}
                    className="p-2 rounded-full hover:bg-linen-2" data-testid={`budget-tier-edit-${i}`}>
                    <Pencil className="w-4 h-4 text-charcoal/60" strokeWidth={1.75} />
                  </button>
                  <button onClick={() => remove(t)}
                    className="p-2 rounded-full hover:bg-rose-50" data-testid={`budget-tier-delete-${i}`}>
                    <Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDialog && (
        <BudgetTierDialog
          initial={editing}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function BudgetTierDialog({ initial, onClose, onSaved }) {
  const [label, setLabel] = useState(initial?.label || "");
  const [emoji, setEmoji] = useState(initial?.emoji || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [minVal, setMinVal] = useState(initial?.min ?? 0);
  const [maxVal, setMaxVal] = useState(initial?.max ?? 0);
  const [displayOrder, setDisplayOrder] = useState(initial?.display_order ?? 999);
  const [isCustom, setIsCustom] = useState(!!initial?.is_custom);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { toast.error("Label is required"); return; }
    if (!isCustom && Number(maxVal) && Number(maxVal) < Number(minVal)) {
      toast.error("Max must be ≥ Min"); return;
    }
    setSaving(true);
    try {
      const payload = {
        label: label.trim(),
        emoji: emoji.trim(),
        description: description.trim(),
        min: Number(minVal) || 0,
        max: Number(maxVal) || 0,
        display_order: Number(displayOrder) || 999,
        is_custom: isCustom,
      };
      if (initial) await api.patch(`/admin/budget-tiers/${initial.id}`, payload);
      else await api.post("/admin/budget-tiers", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
      onClick={onClose} data-testid="budget-tier-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-md w-full shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Budget tier</p>
            <p className="font-display text-2xl text-charcoal mt-1">
              {initial ? "Edit tier" : "Add tier"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-6 space-y-4">
          <Field label="Emoji">
            <input value={emoji} onChange={(e) => setEmoji(e.target.value)}
              placeholder="🌸"
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="budget-tier-emoji" />
          </Field>
          <Field label="Label">
            <input required autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="budget-tier-label" />
          </Field>
          <Field label="Description">
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Simple & sweet"
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="budget-tier-description" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isCustom} onChange={(e) => setIsCustom(e.target.checked)}
              data-testid="budget-tier-is-custom" />
            <span>This is the Custom tier (customer enters their own Min/Max)</span>
          </label>
          {!isCustom && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min ₹">
                <input type="number" min="0" value={minVal} onChange={(e) => setMinVal(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
                  data-testid="budget-tier-min" />
              </Field>
              <Field label="Max ₹">
                <input type="number" min="0" value={maxVal} onChange={(e) => setMaxVal(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
                  data-testid="budget-tier-max" />
              </Field>
            </div>
          )}
          <Field label="Display order">
            <input type="number" min="0" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="budget-tier-order" />
          </Field>
        </div>
        <footer className="p-6 border-t border-warm flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="rounded-full px-5 py-3 text-sm font-bold text-charcoal bg-white border border-warm hover:border-charcoal transition">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="budget-tier-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
      {label}
    </label>
    {children}
  </div>
);
